import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import ProductBrand from '@/components/feature/ProductBrand';

type Mode = 'login' | 'register';

export default function LoginPage() {
  const { signIn, signUp, signInWithGoogle, signInWithOtp } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Đăng nhập bằng link magic qua email (không cần mật khẩu)
  const [otpMode, setOtpMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email || !password) {
      setError('Vui lòng nhập đầy đủ email và mật khẩu!');
      return;
    }
    if (mode === 'register') {
      if (password !== confirmPassword) {
        setError('Mật khẩu xác nhận không khớp!');
        return;
      }
      if (!displayName.trim()) {
        setError('Vui lòng nhập tên hiển thị!');
        return;
      }
    }
    if (password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự!');
      return;
    }

    setLoading(true);
    if (mode === 'login') {
      const { error: err } = await signIn(email, password);
      if (err) {
        // Show actual error for debugging
        setError(err.message || 'Email hoặc mật khẩu không đúng!');
      } else {
        navigate('/');
      }
    } else {
      const { data, error: err } = await signUp(email, password, displayName.trim());
      if (err) {
        if (err.message.includes('already registered') || err.message.includes('already exists')) {
          setError('Email này đã được đăng ký!');
        } else {
          setError(err.message);
        }
      } else if (data?.session) {
        // Auto-confirmed: user has session immediately
        setSuccess('Đăng ký thành công! Đang chuyển về trang chủ...');
        setTimeout(() => navigate('/'), 800);
      } else if (data?.user) {
        setSuccess('Đăng ký thành công! Vui lòng kiểm tra email để xác nhận tài khoản, sau đó đăng nhập.');
        setTimeout(() => {
          setMode('login');
          setPassword('');
          setConfirmPassword('');
          setDisplayName('');
          setSuccess('');
        }, 2000);
      } else {
        setError('Đăng ký không thành công. Vui lòng thử lại!');
      }
    }
    setLoading(false);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!email) {
      setError('Vui lòng nhập email!');
      return;
    }
    setLoading(true);
    const { error: err } = await signInWithOtp(email);
    setLoading(false);
    if (err) {
      setError(err.message);
    } else {
      setOtpSent(true);
      setSuccess('Đã gửi link đăng nhập về email. Mở email (kể cả mục Spam) và bấm vào link để vào app.');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f7f4] flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-amber-100/60 rounded-full"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-amber-50/80 rounded-full"></div>
      </div>

      <div className="relative w-full max-w-md">
        <ProductBrand />

        {/* Card */}
        <div className="bg-white rounded-2xl p-8">
          {/* ── Đăng nhập bằng mã OTP qua email ── */}
          {otpMode ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <p className="text-sm font-semibold text-[#1a2332]">Đăng nhập không cần mật khẩu</p>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Email</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-mail-line text-base"></i>
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    disabled={otpSent}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors disabled:bg-gray-50"
                  />
                </div>
                {otpSent && (
                  <p className="mt-2 text-xs text-gray-400">Bấm vào link trong email để đăng nhập trực tiếp — link hiệu lực 1 giờ.</p>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                  <span className="w-4 h-4 flex items-center justify-center text-red-500 shrink-0"><i className="ri-error-warning-line text-sm"></i></span>
                  <p className="text-red-600 text-xs">{error}</p>
                </div>
              )}
              {success && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                  <span className="w-4 h-4 flex items-center justify-center text-emerald-500 shrink-0"><i className="ri-checkbox-circle-line text-sm"></i></span>
                  <p className="text-emerald-600 text-xs">{success}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Đang xử lý...
                  </span>
                ) : otpSent ? 'Gửi lại link' : 'Gửi link đăng nhập'}
              </button>

              <div className="flex justify-between text-xs">
                <button
                  type="button"
                  onClick={() => { setOtpMode(false); setOtpSent(false); setError(''); setSuccess(''); }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  ← Quay lại
                </button>
              </div>
            </form>
          ) : (
          <>
          {/* Tab switch */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
            {(['login', 'register'] as Mode[]).map(m => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(''); setSuccess(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  mode === m ? 'bg-white text-[#1a2332]' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {m === 'login' ? 'Đăng nhập' : 'Tạo tài khoản'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">Email</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-mail-line text-base"></i>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                />
              </div>
            </div>

            {/* Display Name (register only) */}
            {mode === 'register' && (
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Tên hiển thị</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-user-smile-line text-base"></i>
                  </span>
                  <input
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="Tên hiển thị"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1.5">Mật khẩu</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-lock-line text-base"></i>
                </span>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Tối thiểu 6 ký tự"
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPass ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
                </button>
              </div>
            </div>

            {/* Forgot password link - login mode only */}
            {mode === 'login' && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs text-[#f59e0b] hover:text-[#d97706] font-medium cursor-pointer whitespace-nowrap"
                >
                  Quên mật khẩu?
                </button>
              </div>
            )}

            {/* Confirm password (register only) */}
            {mode === 'register' && (
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">Xác nhận mật khẩu</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-lock-2-line text-base"></i>
                  </span>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Nhập lại mật khẩu"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#f59e0b] transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center text-red-500 shrink-0"><i className="ri-error-warning-line text-sm"></i></span>
                <p className="text-red-600 text-xs">{error}</p>
              </div>
            )}

            {/* Success */}
            {success && (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center text-emerald-500 shrink-0"><i className="ri-checkbox-circle-line text-sm"></i></span>
                <p className="text-emerald-600 text-xs">{success}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#f59e0b] text-white rounded-xl text-sm font-bold hover:bg-[#d97706] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-base"></i>
                  {mode === 'login' ? 'Đang đăng nhập...' : 'Đang tạo tài khoản...'}
                </span>
              ) : (
                mode === 'login' ? 'Đăng nhập' : 'Tạo tài khoản'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400">hoặc</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Google OAuth */}
          <button
            type="button"
            disabled={loading}
            onClick={async () => {
              setError('');
              setLoading(true);
              const { error: err } = await signInWithGoogle();
              if (err) {
                setError(err.message);
                setLoading(false);
              }
              // Thành công → browser redirect sang Google, không cần xử lý thêm
            }}
            className="w-full py-3 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2.5"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Đăng nhập bằng Google
          </button>

          {/* OTP link */}
          <button
            type="button"
            onClick={() => { setOtpMode(true); setError(''); setSuccess(''); }}
            className="w-full mt-3 text-center text-xs text-gray-400 hover:text-[#f59e0b] transition-colors cursor-pointer"
          >
            Đăng nhập không cần mật khẩu (link qua email) →
          </button>
          </>
          )}
        </div>

        <ProductBrand footer />
      </div>
    </div>
  );
}